import {
  useState, useEffect, useCallback, useMemo, useRef,
} from 'react';
import { useTranslation } from 'react-i18next';

import { getSandboxInfo, getSandboxHandoffs, createSandboxHandoff } from '../../../services/sandboxService';
import { extractListData, formatDateTime } from '../../../utils/Utils';

// Entegrasyonlar > Test Ortamı (sandbox self-servis giriş; tasarım belgesi
// docs/superpowers/specs/2026-10-07-sandbox-self-servis-giris-design.md).
//
// Bilgi ucu (/info) ve giriş günlüğü (/handoffs) AYNI turda çekilir. api.js GET okumalarında
// reject ETMEZ — gerçek sinyal res.data.status; "yüklenemedi" ile "hiç giriş yok" AYRI durumlardır.

// Sunucunun /info yanıtı → { available, sandboxWebUrl, sandboxApiUrl, docsUrl } ya da null.
// `available` boolean değilse bilgi yok sayılır: düğme yalnız AÇIKÇA available=true iken görünür.
export function toSandboxInfo(res) {
  const d = res?.data?.status ? res.data.data : null;
  if (!d || Array.isArray(d) || typeof d.available !== 'boolean') return null;
  return {
    available: d.available,
    sandboxWebUrl: d.sandboxWebUrl || null,
    sandboxApiUrl: d.sandboxApiUrl || null,
    docsUrl: d.docsUrl || null,
  };
}

// Geçiş adresi yalnız http(s) olabilir — yeni sekmeye `javascript:` vb. bir adres YAZILMAZ.
export function isNavigableUrl(url) {
  return typeof url === 'string' && /^https?:\/\//i.test(url);
}

// Geçiş yanıtı → { url, expiresAt } ya da null. 401'de interceptor mutasyonu boş yanıtla ÇÖZER
// (reject etmez); gezilebilir bir adres gelmediyse başarı sayılmaz.
const toHandoff = (res) => {
  const d = res?.data?.status ? res.data.data : null;
  return d && isNavigableUrl(d.url) ? { url: d.url, expiresAt: d.expiresAt || null } : null;
};

export function useSandboxApi() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [fetching, setFetching] = useState(true);
  const [failed, setFailed] = useState(false);
  const [info, setInfo] = useState(null);
  const [data, setData] = useState([]);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const requestSeq = useRef(0);

  useEffect(() => {
    const seq = requestSeq.current + 1;
    requestSeq.current = seq;
    setFetching(true);
    Promise.all([getSandboxInfo(), getSandboxHandoffs()])
      .then(([infoRes, listRes]) => {
        if (seq !== requestSeq.current) return;   // eski isteğin geç gelen yanıtı
        const nextInfo = toSandboxInfo(infoRes);
        const listOk = Boolean(listRes?.data?.status);
        setInfo(nextInfo);
        setData(listOk ? extractListData(listRes) : []);
        setFailed(!nextInfo || !listOk);
      })
      .finally(() => { if (seq === requestSeq.current) setFetching(false); });
  }, [refreshTrigger]);

  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  // Geçiş belgesi üretir; başarıda günlük tazelenir (yeni giriş satırı görünsün).
  const createHandoff = useCallback(async () => {
    try {
      const handoff = toHandoff(await createSandboxHandoff());
      if (handoff) refresh();
      return handoff;
    } catch {
      return null;   // hata toast'ı interceptor'da
    }
  }, [refresh]);

  // Günlük küçük (son 100) ve sayfalanmaz; kolonlar kendi süzgecini/sıralamasını taşımaz.
  const columns = useMemo(() => [
    {
      name: 'userName',
      label: t('sandboxEnv.user'),
      options: { filter: false, sort: false },
    },
    {
      name: 'createDate',
      label: t('sandboxEnv.enteredAt'),
      options: {
        filter: false,
        sort: false,
        customBodyRender: (v) => formatDateTime(v, lang),
      },
    },
  ], [t, lang]);

  return {
    fetching, failed, info, data, columns, refresh, createHandoff,
  };
}

export default useSandboxApi;
